// Library Methods
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";
import { ReportContext } from "src/contexts/reports/ReportContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Utils
import {
  getReportEyeText,
  getTutorialCompletionText,
} from "src/utils/exams/examReportsHelper";
import { getLocalDateTimeWithZone } from "src/utils/date/dateHelper";

type VisualAcuityColumnsType = {
  groupId: number;
};

const useVisualAcuityColumns = ({ groupId }: VisualAcuityColumnsType) => {
  const { t } = useTranslation();
  const { exam } = useContext(ReportContext);
  const { clinicInfo } = useClinicContext();
  const { eyeType, duration, examReport } = useContext(ReportSectionContext);

  const visualAcuityPosition = useMemo(() => {
    const visualAcuitySection = exam?.visualAcuitySections?.find(
      (section) => section.groupId === groupId
    );

    return visualAcuitySection?.position
      ? t(visualAcuitySection?.position)
      : "N/A";
  }, [t, groupId, exam?.visualAcuitySections]);

  const localDateAndTimeWithZone = useMemo(
    () =>
      examReport?.timing?.start
        ? getLocalDateTimeWithZone(
            examReport.timing.start,
            clinicInfo?.timezone
          )
        : { date: "N/A", time: "N/A" },
    [examReport?.timing?.start, clinicInfo?.timezone]
  );

  const visualAcuityScore = useMemo(() => {
    if (examReport?.exam?.algorithm === "BAILEY_LOVIE") {
      let scoreString = "";
      examReport?.postProcessing?.processedCharts?.forEach((processedChart) => {
        if (!scoreString.length) {
          scoreString += processedChart?.result?.split(" ")?.[0];
        } else {
          scoreString += ` / ${processedChart?.result?.split(" ")?.[0]}`;
        }
      });
      return scoreString;
    }
    return examReport?.postProcessing?.processedCharts?.[0]?.result ?? "";
  }, [examReport?.exam?.algorithm, examReport?.postProcessing]);

  const visualAcuityColumns = useMemo(() => {
    const firstColumn = [
      {
        title: t("Eye"),
        value: getReportEyeText(eyeType, t),
        isBlueText: true,
      },
      {
        title: t("Position"),
        value: visualAcuityPosition,
        isBlueText: true,
      },
    ];

    const secondColumn = [
      {
        title: t("Score"),
        value: visualAcuityScore,
        isBlueText: true,
      },
      {
        title: t("Tutorial"),
        value: getTutorialCompletionText(
          examReport?.postProcessing?.tutorialCompletionState,
          t
        ),
        isBlueText: false,
      },
    ];

    const thirdColumn = [
      { title: t("Date"), value: localDateAndTimeWithZone?.date },
      { title: t("Time_hour"), value: localDateAndTimeWithZone?.time },
      {
        title: t("Duration"),
        value: duration,
        isBlueText: true,
      },
    ];

    return { firstColumn, secondColumn, thirdColumn };
  }, [
    examReport?.postProcessing?.tutorialCompletionState,
    visualAcuityPosition,
    localDateAndTimeWithZone,
    visualAcuityScore,
    eyeType,
    duration,
    t,
  ]);

  return { visualAcuityColumns };
};

export default useVisualAcuityColumns;
